import { useState, useEffect } from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Pagination } from 'rsuite';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { ActionIconButton } from '@commonClient2/components';
import BiddingResultsTable from './BiddingResultsTable';
import ReportFilters from '../ReportFilters';
import {
    loadEmployeeBiddingResults, UPDATE_EMPLOYEE_BIDDING_RESULTS_REPORT, downloadReportAsCsv,
} from '../../../store/employeeBiddingResultsReport';
import { DAY_COLUMNS_COUNT } from '../../../Manager/BidProfileEdit/ScheduleSelection/ShiftlineTable/ShiftlineTable';
import { EmployeeBiddingResultColumn, EmployeeBiddingResultColumnNames, ShiftDisplayType } from '../../../Enums';
import './EmployeeBiddingResultsReport.scss';

/** @typedef {'asc'|'desc'} SortType */
/** @type {SortType} */
const defaultSortType = 'asc';

const pageSizeOptions = [10, 25, 50, 100];
const initialPageSize = 10;

/**
 * @param {number} page
 * @param {number} totalCount
 * @param {number} pageSize
 * @returns number
 */
const autoFixOutOfBound = (page, totalCount, pageSize) => Math.min(Math.max(page, 1), Math.ceil(totalCount / pageSize));

/**
 * @param {{
 * profileId: string,
 * showAwardedBid: boolean,
 * } & import("../EmployeeReport").ReportProps} param0
 */
function EmployeeBiddingResultsReport({
    profileId,
    dataviewEmployeeData,
    dataviewFields,
    anchorDay,
    isRebid,
    showAwardedBid,
    dayCount,
    isPeriodTypeDay,
    employeeFilters,
    onClose,
}) {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'common.reports' });
    const dispatch = useDispatch();
    const isRankReport = !showAwardedBid;

    /** @type {ShiftBidding.EmployeeStore} */
    // @ts-ignore
    const {
        employeeBiddingResultsReport,
        // @ts-ignore
        pendingTasks,
    } = useSelector((state) => state);

    const {
        preparedEmployeeResults,
        filters,
        pagination,
        sort,
        shifts: shiftsDictionary,
        shiftDisplayType = ShiftDisplayType.ShiftLabel,
    } = employeeBiddingResultsReport;

    const { search, selectedEmployees } = filters;
    const { pageIndex, pageSize, totalCount } = pagination;
    const { sortColumn, isSortAscending } = sort;

    /** @type {[SortType, Function]} */
    const [sortType, setSortType] = useState(defaultSortType);
    const [tableHeight, setTableHeight] = useState(0);

    /**
     * @param {Partial<EmployeeBiddingResultsReport.IEmployeeReportFilters>} filterProps
     */
    const onChangeFilterProperty = (filterProps) => {
        dispatch({
            type: UPDATE_EMPLOYEE_BIDDING_RESULTS_REPORT,
            payload: {
                filters: {
                    ...filters,
                    ...filterProps,
                },
            },
        });
    };

    /**
     * @param {Partial<EmployeeBiddingResultsReport.IPagination>} pageProps
     */
    const onChangePageProperty = (pageProps) => {
        dispatch({
            type: UPDATE_EMPLOYEE_BIDDING_RESULTS_REPORT,
            payload: {
                pagination: {
                    ...pagination,
                    ...pageProps,
                },
            },
        });
    };

    /**
     * @param {Partial<EmployeeBiddingResultsReport.ISort>} sortProps
     */
    const onChangeSortProperty = (sortProps) => {
        if (!pendingTasks) {
            dispatch({
                type: UPDATE_EMPLOYEE_BIDDING_RESULTS_REPORT,
                payload: {
                    sort: {
                        ...sort,
                        ...sortProps,
                    },
                },
            });
        }
    };

    /**
     * @param {number} size
     */
    const handleChangePageSize = (size) => {
        localStorage.setItem('employeeBiddingResultPageSize', `${size}`);
        onChangePageProperty({
            pageSize: size,
            pageIndex: autoFixOutOfBound(pageIndex, totalCount, size),
        });
    };

    /**
     * @param {number} page
     */
    const handleChangePageIndex = (page) => {
        page = autoFixOutOfBound(page, totalCount, pageSize);
        if (page !== pageIndex) {
            onChangePageProperty({ pageIndex: page });
        }
    };

    const fetchEmployeeBiddingResultsData = () => {
        if (selectedEmployees.length && !pendingTasks) {
            dispatch(loadEmployeeBiddingResults(profileId, {
                employeeIds: selectedEmployees,
                searchQuery: search,
                pageIndex: pageIndex - 1,
                pageSize,
                sortColumn,
                isSortAscending,
                dataviewKeyAlias: dataviewFields.filter((x) => x.checked).map((x) => x.key),
                isRankReport,
            }));
        }
    };

    /**
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems[]} items
     * @returns {number}
     */
    const calculateRowHeight = (items) => {
        const shiftlineSelectionRowPadding = 20;
        const shiftlineSelectionShiftHeight = 35;
        const shiftlineSelectionMinRowHeight = 55;

        const maxNbWeek = Math.max(...items.map((x) => x.nbWeek));
        const sumOfWeeklyMax = items.map((x) => x.weeklyHeight.reduce((prev, curr) => prev + (curr || 1), 0));
        const maxHeightUnits = Math.max(maxNbWeek, ...sumOfWeeklyMax);
        const calculatedRowHeight = shiftlineSelectionRowPadding + (maxHeightUnits * shiftlineSelectionShiftHeight);
        return Math.max(calculatedRowHeight, shiftlineSelectionMinRowHeight);
    };

    const handleApply = () => {
        fetchEmployeeBiddingResultsData();
    };

    useEffect(() => {
        fetchEmployeeBiddingResultsData();
    }, [sort, pageIndex, pageSize]);

    useEffect(() => {
        if (!preparedEmployeeResults.length) {
            fetchEmployeeBiddingResultsData();
        }
    }, [selectedEmployees]);

    useEffect(() => {
        if (!selectedEmployees.length) {
            const eeids = _.cloneDeep(employeeFilters.map((x) => x.employeeId));
            dispatch({
                type: UPDATE_EMPLOYEE_BIDDING_RESULTS_REPORT,
                payload: {
                    filters: {
                        search: '',
                        employees: eeids,
                        selectedEmployees: eeids,
                    },
                    pagination: {
                        pageIndex: 1,
                        pageSize: parseInt(localStorage.getItem('employeeBiddingResultPageSize') || `${initialPageSize}`, 10),
                    },
                    sort: {
                        sortColumn: 0,
                        isSortAscending: true,
                    },
                },
            });
        }
        return (() => {
            dispatch({
                type: UPDATE_EMPLOYEE_BIDDING_RESULTS_REPORT,
                payload: {
                    filters: {
                        ...filters,
                        employees: [],
                        selectedEmployees: [],
                    },
                    pagination,
                    sort,
                },
            });
        });
    }, []);

    const setEmployeeTableHeight = () => {
        const height = (document.getElementsByClassName('brt-detailed-selection')[0]?.scrollHeight ?? 500) - 40;
        setTableHeight(height);
    };

    useEffect(() => {
        setEmployeeTableHeight();

        window.addEventListener('scroll', setEmployeeTableHeight, false);
        return () => {
            window.removeEventListener('scroll', setEmployeeTableHeight);
        };
    }, []);

    return (
        <>
            <div className="employee-bidding-results-report-header">
                <span className="header-title">{t(`${showAwardedBid ? 'biddingResults' : 'employeeRanks'}.title`)}</span>
                <ActionIconButton icon={faTimes} onClick={onClose} />
            </div>
            <ReportFilters
                profileId={profileId}
                dataviewFields={dataviewFields}
                hasDataviews={!!dataviewEmployeeData.length}
                allEmployeeOptionsInFilter={employeeFilters}
                selectedEmployees={selectedEmployees}
                onChangeSelectedEmployees={(employeeIds) => onChangeFilterProperty({ selectedEmployees: employeeIds })}
                downloadReportAsCsv={(_profileId, employeeIds, selectedDataviews, searchString) =>
                    downloadReportAsCsv(_profileId, employeeIds, selectedDataviews, searchString, isRankReport)}
                searchString={search}
                onChangeSearchString={(searchString) => onChangeFilterProperty({ search: searchString })}
                onChangeDataviewColumns={(dataviewColumnIds) => onChangeFilterProperty({ dataviewColumnsFilter: dataviewColumnIds })}
                handleApplyFilter={handleApply}
            />
            <BiddingResultsTable
                data={preparedEmployeeResults.slice(0, pageSize)}
                height={tableHeight}
                sortColumn={EmployeeBiddingResultColumnNames[sortColumn]}
                setSortColumn={(column) => onChangeSortProperty({
                    sortColumn: EmployeeBiddingResultColumn[column],
                    isSortAscending: sortColumn !== EmployeeBiddingResultColumn[column] || !isSortAscending,
                })}
                sortType={sortType}
                setSortType={setSortType}
                dataviewFields={dataviewFields}
                dataviewEmployeeData={dataviewEmployeeData}
                isRebid={isRebid}
                // @ts-ignore
                rowHeight={isRankReport
                    ? 45
                    : calculateRowHeight(preparedEmployeeResults.map((x) => x.groupedAwardedItem).filter((x) => x !== null))}
                anchorDay={anchorDay}
                shiftsDictionary={shiftsDictionary}
                loading={!!pendingTasks}
                showAwardedBid={showAwardedBid}
                dayCount={dayCount}
                isPeriodTypeDay={isPeriodTypeDay}
                daysPerPeriod={DAY_COLUMNS_COUNT}
                shiftDisplayType={shiftDisplayType}
            />
            <div className="pagination-wrapper">
                <div className="pagination-desktop">
                    <Pagination
                        prev
                        next
                        first
                        last
                        ellipsis
                        boundaryLinks
                        maxButtons={5}
                        size="xs"
                        layout={['total', '-', 'pager', 'skip', '|', 'limit']}
                        total={totalCount}
                        limitOptions={pageSizeOptions}
                        limit={pageSize}
                        onChangeLimit={(limit) => handleChangePageSize(limit)}
                        activePage={pageIndex}
                        onChangePage={(page) => handleChangePageIndex(page)}
                    />
                </div>
                <div className="pagination-mobile">
                    <div className="pagination-mobile-layout">
                        <Pagination
                            prev
                            next
                            first
                            last
                            ellipsis
                            boundaryLinks
                            maxButtons={3}
                            size="xs"
                            layout={['total', '-', 'pager']}
                            total={totalCount}
                            limit={pageSize}
                            activePage={pageIndex}
                            onChangePage={(page) => handleChangePageIndex(page)}
                        />

                        <Pagination
                            size="xs"
                            layout={['skip', 'limit']}
                            total={totalCount}
                            limitOptions={pageSizeOptions}
                            limit={pageSize}
                            activePage={pageIndex}
                            onChangePage={(page) => handleChangePageIndex(page)}
                            onChangeLimit={(limit) => handleChangePageSize(limit)}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default EmployeeBiddingResultsReport;
